import { useState, useEffect, useCallback } from 'react';
import { closingReportApi, ClosingReport, ClosingReportSummary, FamilyAgg } from '../api/closing-report.api';
import { posApi } from '../api/pos.api';
import { ouvrirDocumentPrive, recupererDocumentPrive } from '../utils/documentPrive';
import { hardwareBridgeA4 } from '../services/printers/HardwareBridgeAdapter';

const PAGE_SIZE = 30;

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2) + ' €';
}
function eur(cents: number): string {
  return (cents / 100).toFixed(2);
}
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-BE');
}

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function presetRange(kind: string): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (kind === 'week') {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: ymd(monday), to: ymd(sunday) };
  }
  if (kind === 'month') return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
  if (kind === 'lastmonth') return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
  if (kind === 'quarter') {
    const q = Math.floor(m / 3);
    return { from: ymd(new Date(y, q * 3, 1)), to: ymd(new Date(y, q * 3 + 3, 0)) };
  }
  if (kind === 'lastquarter') {
    const q = Math.floor(m / 3) - 1;
    const yy = q < 0 ? y - 1 : y;
    const qq = (q + 4) % 4;
    return { from: ymd(new Date(yy, qq * 3, 1)), to: ymd(new Date(yy, qq * 3 + 3, 0)) };
  }
  return { from: ymd(now), to: ymd(now) };
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between py-1 ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function FamilyCard({ title, agg, emphasizeCost }: { title: string; agg: FamilyAgg; emphasizeCost?: boolean }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      <div className="text-sm space-y-1">
        <Row label="Nombre" value={String(agg.count)} />
        <Row label="Coût d'achat HT" value={formatCents(agg.cost_cents)} strong={emphasizeCost} />
        <Row label="Valeur de vente TTC" value={formatCents(agg.sale_cents)} />
        <Row label="TVA (sur prix d'achat)" value={formatCents(agg.vat_cents)} />
      </div>
    </div>
  );
}

function exportCsv(s: ClosingReportSummary) {
  const L: string[] = [];
  L.push(`Synthèse comptable;${s.from} au ${s.to}`);
  L.push(`Nombre de rapports Z;${s.reports_count}`);
  L.push('');
  L.push('CHIFFRE D’AFFAIRES;');
  L.push(`Nb ventes;${s.sales.count}`);
  L.push(`CA TTC;${eur(s.sales.ttc_cents)}`);
  L.push(`CA HT;${eur(s.sales.ht_cents)}`);
  L.push(`TVA collectée;${eur(s.sales.tax_cents)}`);
  L.push('');
  L.push('CA TOTAL (CANAUX);HT;TTC;TVA');
  L.push(`Magasin (POS);${eur(s.channels.retail.ht_cents)};${eur(s.channels.retail.ttc_cents)};${eur(s.channels.retail.vat_cents)}`);
  L.push(`B2B facturé (Enfact);${eur(s.channels.b2b.ht_cents)};${eur(s.channels.b2b.ttc_cents)};${eur(s.channels.b2b.vat_cents)}`);
  L.push(`E-commerce;${eur(s.channels.ecom.ht_cents)};${eur(s.channels.ecom.ttc_cents)};${eur(s.channels.ecom.vat_cents)}`);
  L.push(`TOTAL;${eur(s.channels.total.ht_cents)};${eur(s.channels.total.ttc_cents)};${eur(s.channels.total.vat_cents)}`);
  L.push('');
  L.push('VENTILATION TVA;Base HT;Montant TVA');
  L.push(`0%;${eur(s.vat.base_0_cents)};`);
  L.push(`6%;${eur(s.vat.base_6_cents)};${eur(s.vat.amount_6_cents)}`);
  L.push(`21%;${eur(s.vat.base_21_cents)};${eur(s.vat.amount_21_cents)}`);
  L.push('');
  L.push('PAIEMENTS;');
  L.push(`Espèces;${eur(s.payments.cash_cents)}`);
  L.push(`Bancontact;${eur(s.payments.bancontact_cents)}`);
  L.push(`Titres-repas;${eur(s.payments.meal_voucher_cents)}`);
  L.push(`Wero;${eur(s.payments.wero_cents)}`);
  L.push(`Wallet;${eur(s.payments.wallet_cents)}`);
  L.push(`En ligne;${eur(s.payments.online_cents)}`);
  L.push(`Virement;${eur(s.payments.virement_cents)}`);
  L.push(`Autre;${eur(s.payments.other_cents)}`);
  L.push('');
  L.push('DIVERS;');
  L.push(`Écart de caisse cumulé;${eur(s.cash_difference_cents)}`);
  L.push(`Remises;${eur(s.discounts.total_cents)} (${s.discounts.count})`);
  L.push(`Annulations;${eur(s.cancellations.total_cents)} (${s.cancellations.count})`);
  L.push(`Remboursements;${eur(s.refunds.total_cents)} (${s.refunds.count})`);
  L.push(`Retraits e-commerce;${eur(s.ecom_pickup.total_cents)} (${s.ecom_pickup.count})`);
  L.push('');
  L.push('SORTIES DE STOCK;Nb;Coût HT;Vente TTC;TVA (sur prix d\'achat)');
  const f = s.stock_outs.by_family;
  const famRow = (t: string, a: FamilyAgg) => `${t};${a.count};${eur(a.cost_cents)};${eur(a.sale_cents)};${eur(a.vat_cents)}`;
  L.push(famRow('Prélèvements', f.prelevements));
  L.push(famRow('Pertes / démarque', f.pertes));
  L.push(famRow('Dons / sponsoring', f.dons));
  L.push(famRow('Retours fournisseur', f.retours));
  L.push('');
  L.push('PRÉLÈVEMENTS PAR PERSONNE;Nb;Coût HT;Vente TTC;TVA (sur prix d\'achat)');
  s.stock_outs.prelevements_by_person.forEach((p) =>
    L.push(`${p.personne};${p.count};${eur(p.cost_cents)};${eur(p.sale_cents)};${eur(p.vat_cents)}`),
  );

  const blob = new Blob(['﻿' + L.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `synthese-Z-${s.from}_${s.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function printSummary(s: ClosingReportSummary) {
  const w = window.open('', '_blank');
  if (!w) return;
  const f = s.stock_outs.by_family;
  const famRow = (t: string, a: FamilyAgg) =>
    `<tr><td>${t}</td><td class="r">${a.count}</td><td class="r">${eur(a.cost_cents)} €</td><td class="r">${eur(a.sale_cents)} €</td><td class="r">${eur(a.vat_cents)} €</td></tr>`;
  const persRows =
    s.stock_outs.prelevements_by_person
      .map((p) => `<tr><td>${p.personne}</td><td class="r">${p.count}</td><td class="r">${eur(p.cost_cents)} €</td><td class="r">${eur(p.sale_cents)} €</td><td class="r">${eur(p.vat_cents)} €</td></tr>`)
      .join('') || '<tr><td colspan="5">Aucun prélèvement</td></tr>';
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Synthèse Z ${s.from} au ${s.to}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:12px}
    h1{color:#065f46;font-size:20px;margin:0 0 4px}
    h2{font-size:14px;color:#065f46;border-bottom:1px solid #d1d5db;padding-bottom:4px;margin:18px 0 8px}
    .sub{color:#555;margin-bottom:12px}
    table{width:100%;border-collapse:collapse;margin-bottom:8px}
    td,th{padding:5px 8px;border-bottom:1px solid #eee;text-align:left}
    th{background:#f3f4f6;font-size:11px;text-transform:uppercase;color:#555}
    .r{text-align:right}
    .grid{display:flex;gap:24px}
    .grid>div{flex:1}
    .warn{background:#fffbeb;border:1px solid #fde68a;color:#92400e;padding:8px;border-radius:4px;margin-bottom:12px}
    .tot{font-weight:bold}
    @media print{button{display:none}}
  </style></head><body>
  <h1>Synthèse comptable — Les Folies Maraîchères</h1>
  <div class="sub">Période du ${new Date(s.from).toLocaleDateString('fr-BE')} au ${new Date(s.to).toLocaleDateString('fr-BE')} · ${s.reports_count} rapport(s) Z</div>
  ${s.stock_outs.without_cost_count > 0 ? `<div class="warn">⚠️ ${s.stock_outs.without_cost_count} sortie(s) de stock sans coût d'achat renseigné — montants au coût sous-évalués.</div>` : ''}
  <h2>Chiffre d'affaires total (tous canaux)</h2>
  <table>
    <tr><th>Canal</th><th class="r">HT</th><th class="r">TTC</th><th class="r">TVA</th></tr>
    <tr><td>Magasin (POS)</td><td class="r">${eur(s.channels.retail.ht_cents)} €</td><td class="r">${eur(s.channels.retail.ttc_cents)} €</td><td class="r">${eur(s.channels.retail.vat_cents)} €</td></tr>
    <tr><td>B2B facturé (Enfact)</td><td class="r">${eur(s.channels.b2b.ht_cents)} €</td><td class="r">${eur(s.channels.b2b.ttc_cents)} €</td><td class="r">${eur(s.channels.b2b.vat_cents)} €</td></tr>
    <tr><td>E-commerce</td><td class="r">${eur(s.channels.ecom.ht_cents)} €</td><td class="r">${eur(s.channels.ecom.ttc_cents)} €</td><td class="r">${eur(s.channels.ecom.vat_cents)} €</td></tr>
    <tr class="tot"><td>TOTAL</td><td class="r">${eur(s.channels.total.ht_cents)} €</td><td class="r">${eur(s.channels.total.ttc_cents)} €</td><td class="r">${eur(s.channels.total.vat_cents)} €</td></tr>
  </table>
  <div class="grid">
    <div>
      <h2>Chiffre d'affaires</h2>
      <table>
        <tr><td>Nombre de ventes</td><td class="r">${s.sales.count}</td></tr>
        <tr><td>CA TTC</td><td class="r tot">${eur(s.sales.ttc_cents)} €</td></tr>
        <tr><td>CA HT</td><td class="r">${eur(s.sales.ht_cents)} €</td></tr>
        <tr><td>TVA collectée</td><td class="r">${eur(s.sales.tax_cents)} €</td></tr>
      </table>
      <h2>Ventilation TVA</h2>
      <table>
        <tr><th>Taux</th><th class="r">Base HT</th><th class="r">TVA</th></tr>
        <tr><td>0 %</td><td class="r">${eur(s.vat.base_0_cents)} €</td><td class="r">—</td></tr>
        <tr><td>6 %</td><td class="r">${eur(s.vat.base_6_cents)} €</td><td class="r">${eur(s.vat.amount_6_cents)} €</td></tr>
        <tr><td>21 %</td><td class="r">${eur(s.vat.base_21_cents)} €</td><td class="r">${eur(s.vat.amount_21_cents)} €</td></tr>
      </table>
    </div>
    <div>
      <h2>Moyens de paiement</h2>
      <table>
        <tr><td>Espèces</td><td class="r">${eur(s.payments.cash_cents)} €</td></tr>
        <tr><td>Bancontact</td><td class="r">${eur(s.payments.bancontact_cents)} €</td></tr>
        <tr><td>Titres-repas</td><td class="r">${eur(s.payments.meal_voucher_cents)} €</td></tr>
        <tr><td>Wero</td><td class="r">${eur(s.payments.wero_cents)} €</td></tr>
        <tr><td>Wallet</td><td class="r">${eur(s.payments.wallet_cents)} €</td></tr>
        <tr><td>En ligne</td><td class="r">${eur(s.payments.online_cents)} €</td></tr>
        <tr><td>Virement</td><td class="r">${eur(s.payments.virement_cents)} €</td></tr>
        <tr><td>Autre</td><td class="r">${eur(s.payments.other_cents)} €</td></tr>
      </table>
      <h2>Caisse & mouvements</h2>
      <table>
        <tr><td>Écart de caisse cumulé</td><td class="r">${eur(s.cash_difference_cents)} €</td></tr>
        <tr><td>Remises (${s.discounts.count})</td><td class="r">${eur(s.discounts.total_cents)} €</td></tr>
        <tr><td>Annulations (${s.cancellations.count})</td><td class="r">${eur(s.cancellations.total_cents)} €</td></tr>
        <tr><td>Remboursements (${s.refunds.count})</td><td class="r">${eur(s.refunds.total_cents)} €</td></tr>
        <tr><td>Retraits e-commerce (${s.ecom_pickup.count})</td><td class="r">${eur(s.ecom_pickup.total_cents)} €</td></tr>
      </table>
    </div>
  </div>
  <h2>Sorties de stock</h2>
  <table>
    <tr><th>Famille</th><th class="r">Nb</th><th class="r">Coût HT</th><th class="r">Vente TTC</th><th class="r">TVA (sur prix d'achat)</th></tr>
    ${famRow('Prélèvements', f.prelevements)}
    ${famRow('Pertes / démarque', f.pertes)}
    ${famRow('Dons / sponsoring', f.dons)}
    ${famRow('Retours fournisseur', f.retours)}
  </table>
  <h2>Prélèvements par personne</h2>
  <table>
    <tr><th>Personne</th><th class="r">Nb</th><th class="r">Coût HT</th><th class="r">Vente TTC</th><th class="r">TVA (sur prix d'achat)</th></tr>
    ${persRows}
  </table>
  <button onclick="window.print()" style="margin-top:16px;padding:8px 16px;background:#065f46;color:#fff;border:none;border-radius:4px;cursor:pointer">Imprimer / Enregistrer en PDF</button>
  </body></html>`;
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 350);
}

export default function ClosingReportHistory() {
  const [tab, setTab] = useState<'synthese' | 'historique'>('synthese');

  // --- Onglet Synthèse ---
  const initial = presetRange('month');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [summary, setSummary] = useState<ClosingReportSummary | null>(null);
  const [sumLoading, setSumLoading] = useState(false);
  const [sumError, setSumError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  /**
   * Ventes tombées dans une journée déjà clôturée : encaissées, valides, et
   * dans aucun rapport Z. Elles n'étaient signalées nulle part — la seule
   * trace était une métadonnée que personne ne lit. Elles se voient ici.
   */
  const [horsZ, setHorsZ] = useState<{ count: number; total_cents: number } | null>(null);
  useEffect(() => {
    posApi
      .getSalesHorsZ()
      .then((r) => setHorsZ({ count: r.count, total_cents: r.total_cents }))
      .catch(() => setHorsZ(null));
  }, []);

  const loadSummary = useCallback(async (f: string, t: string) => {
    setSumLoading(true);
    setSumError(null);
    try {
      const data = await closingReportApi.getSummary(f, t);
      setSummary(data);
    } catch (err: any) {
      setSumError(err.message || 'Erreur lors de la synthèse');
      setSummary(null);
    } finally {
      setSumLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'synthese' && !summary) loadSummary(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const applyPreset = (kind: string) => {
    const r = presetRange(kind);
    setFrom(r.from);
    setTo(r.to);
    loadSummary(r.from, r.to);
  };

  const handleSyncEnfact = async () => {
    setSyncing(true);
    try {
      const res = await closingReportApi.syncEnfact(from, to);
      await loadSummary(from, to);
      alert(`Synchronisation Enfact : ${res.imported} facture(s) importée(s), ${res.skipped} déjà présente(s).`);
    } catch (e: any) {
      alert('❌ ' + (e?.message || 'Synchronisation Enfact échouée'));
    } finally {
      setSyncing(false);
    }
  };

  // --- Onglet Historique ---
  // Imprimante A4 du poste (caisse) : réimprimer un Z passé d'un geste.
  const [a4Dispo, setA4Dispo] = useState(false);
  const [impression, setImpression] = useState<string | null>(null);
  useEffect(() => {
    hardwareBridgeA4.isAvailable().then(setA4Dispo);
  }, []);
  const imprimerA4 = async (report: ClosingReport) => {
    if (!report.pdf_url || impression) return;
    setImpression(report.id);
    try {
      const r = await hardwareBridgeA4.printPdf(await recupererDocumentPrive(report.pdf_url), `Rapport ${report.report_number}`);
      if (!r.success) throw new Error(r.message);
    } catch (e: any) {
      alert(`Impression A4 impossible : ${e?.message || 'erreur inconnue'}`);
    } finally {
      setImpression(null);
    }
  };
  const [reports, setReports] = useState<ClosingReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchReports = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await closingReportApi.list(PAGE_SIZE, pageNum * PAGE_SIZE);
      const data = Array.isArray(response) ? response : (response as any).reports || [];
      setReports(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'historique') fetchReports(page);
  }, [tab, page, fetchReports]);

  const PRESETS: { key: string; label: string }[] = [
    { key: 'week', label: 'Cette semaine' },
    { key: 'month', label: 'Ce mois' },
    { key: 'lastmonth', label: 'Mois dernier' },
    { key: 'quarter', label: 'Ce trimestre' },
    { key: 'lastquarter', label: 'Trimestre dernier' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Rapports Z</h1>
        <p className="mt-2 text-gray-600">Synthèse comptable par période et historique des clôtures de caisse</p>
      </div>

      {horsZ && horsZ.count > 0 && (
        <div className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <p className="font-bold text-amber-900">
            {horsZ.count} vente{horsZ.count > 1 ? 's' : ''} hors rapport Z — {formatCents(horsZ.total_cents)}
          </p>
          <p className="text-amber-900 mt-1">
            Ces ventes ont été encaissées dans une journée déjà clôturée : elles sont valides,
            mais n'entrent dans aucun rapport et ne peuvent plus y entrer. Cela arrive quand une
            caisse encaisse après la clôture, ou qu'un poste rentré du marché synchronise ses
            ventes une fois le Z parti. À signaler au comptable.
          </p>
        </div>
      )}

      {/* Onglets */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {(['synthese', 'historique'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'synthese' ? 'Synthèse' : 'Historique'}
          </button>
        ))}
      </div>

      {/* ===== SYNTHÈSE ===== */}
      {tab === 'synthese' && (
        <div className="space-y-6">
          {/* Contrôles période */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex flex-wrap items-end gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {p.label}
                </button>
              ))}
              <div className="flex flex-wrap items-end gap-2 sm:ml-auto">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Du</label>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Au</label>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" />
                </div>
                <button onClick={() => loadSummary(from, to)} className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium">
                  Générer
                </button>
                <button
                  onClick={handleSyncEnfact}
                  disabled={syncing}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
                  title="Rapatrier les factures Enfact de la période (sans toucher au stock)"
                >
                  {syncing ? 'Synchro…' : '🔄 Synchroniser Enfact'}
                </button>
              </div>
            </div>
          </div>

          {sumError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{sumError}</div>}
          {sumLoading ? (
            <div className="p-8 text-center text-gray-500">Chargement…</div>
          ) : summary ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-600">
                  Période du <strong>{formatDate(summary.from)}</strong> au <strong>{formatDate(summary.to)}</strong> · {summary.reports_count} rapport(s) Z
                </div>
                <div className="flex gap-2">
                  <button onClick={() => printSummary(summary)} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium">
                    🖨️ PDF / Imprimer
                  </button>
                  <button onClick={() => exportCsv(summary)} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50">
                    CSV
                  </button>
                </div>
              </div>

              {/* CA total multi-canal */}
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Chiffre d'affaires total (tous canaux)</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b">
                      <th className="text-left py-1">Canal</th>
                      <th className="text-right py-1">Nb</th>
                      <th className="text-right py-1">HT</th>
                      <th className="text-right py-1">TTC</th>
                      <th className="text-right py-1">TVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b"><td className="py-1.5">Magasin (POS)</td><td className="text-right">{summary.channels.retail.count ?? '—'}</td><td className="text-right">{formatCents(summary.channels.retail.ht_cents)}</td><td className="text-right">{formatCents(summary.channels.retail.ttc_cents)}</td><td className="text-right">{formatCents(summary.channels.retail.vat_cents)}</td></tr>
                    <tr className="border-b"><td className="py-1.5">B2B facturé (Enfact)</td><td className="text-right">{summary.channels.b2b.count ?? '—'}</td><td className="text-right">{formatCents(summary.channels.b2b.ht_cents)}</td><td className="text-right">{formatCents(summary.channels.b2b.ttc_cents)}</td><td className="text-right">{formatCents(summary.channels.b2b.vat_cents)}</td></tr>
                    <tr className="border-b"><td className="py-1.5">E-commerce</td><td className="text-right">{summary.channels.ecom.count ?? '—'}</td><td className="text-right">{formatCents(summary.channels.ecom.ht_cents)}</td><td className="text-right">{formatCents(summary.channels.ecom.ttc_cents)}</td><td className="text-right">{formatCents(summary.channels.ecom.vat_cents)}</td></tr>
                    <tr className="font-bold text-gray-900"><td className="py-2">TOTAL</td><td></td><td className="text-right">{formatCents(summary.channels.total.ht_cents)}</td><td className="text-right">{formatCents(summary.channels.total.ttc_cents)}</td><td className="text-right">{formatCents(summary.channels.total.vat_cents)}</td></tr>
                  </tbody>
                </table>
              </div>

              {summary.stock_outs.without_cost_count > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  ⚠️ {summary.stock_outs.without_cost_count} sortie(s) de stock <strong>sans coût d'achat renseigné</strong> sur la période — les montants au coût sont sous-évalués tant qu'elles ne sont pas complétées.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CA */}
                <div className="bg-white rounded-lg shadow p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Chiffre d'affaires</h3>
                  <Row label="Nombre de ventes" value={String(summary.sales.count)} />
                  <Row label="CA TTC" value={formatCents(summary.sales.ttc_cents)} strong />
                  <Row label="CA HT" value={formatCents(summary.sales.ht_cents)} />
                  <Row label="TVA collectée" value={formatCents(summary.sales.tax_cents)} />
                </div>

                {/* TVA */}
                <div className="bg-white rounded-lg shadow p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Ventilation TVA</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs">
                        <th className="text-left py-1">Taux</th>
                        <th className="text-right py-1">Base HT</th>
                        <th className="text-right py-1">Montant TVA</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td className="py-1">0 %</td><td className="text-right">{formatCents(summary.vat.base_0_cents)}</td><td className="text-right">—</td></tr>
                      <tr><td className="py-1">6 %</td><td className="text-right">{formatCents(summary.vat.base_6_cents)}</td><td className="text-right">{formatCents(summary.vat.amount_6_cents)}</td></tr>
                      <tr><td className="py-1">21 %</td><td className="text-right">{formatCents(summary.vat.base_21_cents)}</td><td className="text-right">{formatCents(summary.vat.amount_21_cents)}</td></tr>
                    </tbody>
                  </table>
                </div>

                {/* Paiements */}
                <div className="bg-white rounded-lg shadow p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Moyens de paiement</h3>
                  <Row label="Espèces" value={formatCents(summary.payments.cash_cents)} />
                  <Row label="Bancontact" value={formatCents(summary.payments.bancontact_cents)} />
                  <Row label="Titres-repas" value={formatCents(summary.payments.meal_voucher_cents)} />
                  <Row label="Wero" value={formatCents(summary.payments.wero_cents)} />
                  <Row label="Wallet" value={formatCents(summary.payments.wallet_cents)} />
                  <Row label="En ligne" value={formatCents(summary.payments.online_cents)} />
                  <Row label="Virement" value={formatCents(summary.payments.virement_cents)} />
                  <Row label="Autre" value={formatCents(summary.payments.other_cents)} />
                </div>

                {/* Divers */}
                <div className="bg-white rounded-lg shadow p-5">
                  <h3 className="font-semibold text-gray-900 mb-3">Caisse & mouvements</h3>
                  <Row label="Écart de caisse cumulé" value={formatCents(summary.cash_difference_cents)} />
                  <Row label={`Remises (${summary.discounts.count})`} value={formatCents(summary.discounts.total_cents)} />
                  <Row label={`Annulations (${summary.cancellations.count})`} value={formatCents(summary.cancellations.total_cents)} />
                  <Row label={`Remboursements (${summary.refunds.count})`} value={formatCents(summary.refunds.total_cents)} />
                  <Row label={`Retraits e-commerce (${summary.ecom_pickup.count})`} value={formatCents(summary.ecom_pickup.total_cents)} />
                </div>
              </div>

              {/* Sorties de stock par famille */}
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Sorties de stock</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FamilyCard title="Prélèvements" agg={summary.stock_outs.by_family.prelevements} emphasizeCost />
                  <FamilyCard title="Pertes / démarque" agg={summary.stock_outs.by_family.pertes} />
                  <FamilyCard title="Dons / sponsoring" agg={summary.stock_outs.by_family.dons} />
                  <FamilyCard title="Retours fournisseur" agg={summary.stock_outs.by_family.retours} />
                </div>
              </div>

              {/* Prélèvements par personne */}
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Prélèvements par personne</h3>
                {summary.stock_outs.prelevements_by_person.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucun prélèvement sur la période.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs border-b">
                        <th className="text-left py-1">Personne</th>
                        <th className="text-right py-1">Nb</th>
                        <th className="text-right py-1">Coût d'achat HT</th>
                        <th className="text-right py-1">Valeur de vente TTC</th>
                        <th className="text-right py-1">TVA (sur prix d'achat)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.stock_outs.prelevements_by_person.map((p) => (
                        <tr key={p.personne} className="border-b last:border-0">
                          <td className="py-1.5">{p.personne}</td>
                          <td className="text-right">{p.count}</td>
                          <td className="text-right font-medium">{formatCents(p.cost_cents)}</td>
                          <td className="text-right">{formatCents(p.sale_cents)}</td>
                          <td className="text-right">{formatCents(p.vat_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ===== HISTORIQUE ===== */}
      {tab === 'historique' && (
        <>
          {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Chargement...</div>
            ) : reports.length === 0 ? (
              <div className="p-8 text-center text-gray-500">Aucun rapport de clôture trouvé</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Rapport</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ventes</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CA TTC</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">TVA</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Espèces</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bancontact</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opérateur</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reports.map((report) => (
                        <tr key={report.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDate(report.report_date)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{report.report_number}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">{report.total_sales_count}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{formatCents(report.total_sales_cents)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">{formatCents(report.total_tax_cents)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">{formatCents(report.payment_cash_cents)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">{formatCents(report.payment_card_cents)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{report.closed_by_name || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                            {report.pdf_url ? (
                              <button
                                type="button"
                                onClick={() => void ouvrirDocumentPrive(report.pdf_url!)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100"
                              >
                                PDF
                              </button>
                            ) : null}
                            {report.pdf_url && a4Dispo && (
                              <button
                                type="button"
                                onClick={() => void imprimerA4(report)}
                                disabled={impression === report.id}
                                className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-60"
                              >
                                🖨️ {impression === report.id ? 'Impression…' : 'Imprimer A4'}
                              </button>
                            )}
                            {report.pdf_url ? null : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-600">Page {page + 1}</p>
                  <div className="flex gap-2">
                    <button onClick={() => page > 0 && setPage(page - 1)} disabled={page === 0} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      Précédent
                    </button>
                    <button onClick={() => hasMore && setPage(page + 1)} disabled={!hasMore} className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      Suivant
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
